import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Row, TabContent, TabPane, Card, CardBody, Container } from 'reactstrap';
import { changePassword, getUserProfile } from '../../Api/ApiUrlConst';
import ProfileViewComponents from '../../Components/UserProfileComponents/ProfileViewComponents';
import ChangePasswordComponents from '../../Components/UserProfileComponents/ChangePasswordComponents';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import { red } from '@mui/material/colors';

interface UserProfileDTO {
  name: string;
  email: string;
  role: string;
  department: string;
  gender: string;
  phoneNumber: string;
}

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfileDTO | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('UserDetails');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getUserProfile();
        setProfile(response.data);
      } catch (error: any) {
        // setProfileError(error?.response?.data || 'Failed to load profile.');
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    await changePassword({ currentPassword, newPassword });
  };

  const goToDashboard = () => {
    if (profile?.role === 'Admin') {
      navigate('/Admin');
    } else if (profile?.role === 'ProjectManager') {
      navigate('/Project');
    } else if (profile?.role === 'TeamLeader') {
      navigate('/TeamLead');
    } 
     else if (profile?.role === "Client") {
      navigate("/client");
    }else {
      navigate('/Member');
    }
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  if (loadingProfile) return <p>Loading profile...</p>;

  if (profileError) return <p style={{ color: 'red' }}>Error: {profileError}</p>;

  const getAvatar = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase();
  }

  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: '#44b700',
      color: '#44b700',
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: 'ripple 1.2s infinite ease-in-out',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
  }));

  return (
    <Container fluid style={{ padding: '20px' }}>

      <Row>

        {/* Sidebar Navigation */}
        <Col
          sm={12}
          md={3}
          lg={2}
          className="tabmenu"
          style={{ padding: "10px", height: "90vh" }}
        >
          {/* Avatar Section */}
          <div className="avatar-logo text-center">
            <Stack direction="row" justifyContent="center" spacing={2}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
              >
                <Avatar
                  className="logos"
                  sx={{ width: 56, height: 56, bgcolor: red[500] }}
                >
                  <span className="log-txt">{getAvatar(profile?.name || "")}</span>
                </Avatar>
              </StyledBadge>
            </Stack>
            <h1 className="log-txt1 mt-2">{profile?.name}</h1>
          </div>

          <hr style={{ marginTop: "10px" }} />

          {/* Buttons Section */}
          <div className="d-flex flex-column gap-3 mt-3">
            <Button
              color="primary"
              outline
              onClick={goToDashboard}
              style={{ borderRadius: "6px", fontWeight: 600 }}
            >
              Go to Dashboard
            </Button>

            <Button
              color="secondary"
              outline
              onClick={() => toggleTab("UserDetails")}
              style={{ borderRadius: "6px", fontWeight: 600 }}
            >
              User Details
            </Button>

            <Button
              color="secondary"
              outline
              onClick={() => toggleTab("ChangePassword")}
              style={{ borderRadius: "6px", fontWeight: 600 }}
            >
              Change Password
            </Button>
          </div>
        </Col>


        <Col sm={12} md={9} lg={10}>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="UserDetails">
              <Card style={{
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                border: 'none',
                height: '90vh'
              }}>
                <CardBody>
                  {profile && (
                    <>
                      <ProfileViewComponents
                        name={profile.name}
                        email={profile.email}
                        role={profile.role}
                        department={profile.department}
                        gender={profile.gender}
                        phoneNumber={profile.phoneNumber}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </TabPane>
            <TabPane tabId="ChangePassword">
              <Card style={{
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                border: 'none',
                height: '90vh'
              }}>
                <CardBody>
                  <ChangePasswordComponents onChangePassword={handleChangePassword} />
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile;